import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import BackArrow from '../assets/images/Frame_18177.png';
import InactivityTimer from './InactivityTimer';

const AppointmentReceipt = () => {
    const location = useLocation();
    const { appointment } = location.state || {};

    const handlePrint = () => {
        window.print();
    };

    // Format the current date for receipt
    const currentDate = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    // Map gender code to readable format
    const formatGender = (gender) => {
        if (!gender) return 'N/A';
        if (gender === 'M' || gender === 1) return 'Male';
        if (gender === 'F' || gender === 2) return 'Female';
        return gender;
    };

    // Calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob || !/^\d{2}-\d{2}-\d{4}$/.test(dob)) return 'N/A';
        try {
            const [day, month, year] = dob.split('-').map(Number);
            const dobDate = new Date(year, month - 1, day);
            const currentDate = new Date();
            if (isNaN(dobDate.getTime()) || dobDate > currentDate) return 'N/A';

            let age = currentDate.getFullYear() - dobDate.getFullYear();
            const monthDiff = currentDate.getMonth() - dobDate.getMonth();
            const dayDiff = currentDate.getDate() - dobDate.getDate();
            if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age--;
            return `${age} years`;
        } catch {
            return 'N/A';
        }
    };

    return (
        <>
            <InactivityTimer />
            <div className="p-6">
                <style>
                    {`
                        @media print {
                            .no-print {
                                display: none !important;
                            }
                            .receipt-container {
                                background: white;
                                color: black;
                                padding: 12px;
                                width: 50%;
                                margin-left: auto;
                            }
                            .receipt-container h1, .receipt-container h2, .receipt-container p {
                                color: black;
                            }
                            .field-row {
                                border-bottom: 0px solid #ccc;
                            }
                            .field-row:last-child {
                                border-bottom: none;
                            }
                            .field-label, .field-value {
                                font-size: 11px;
                            }
                            .section-header {
                                font-size: 11px;
                            }
                            .receipt-title {
                                font-size: 13px;
                            }
                        }
                    `}
                </style>

                <div className="flex items-center no-print">
                    <Link
                        to="/login"
                        state={{ patientId: appointment?.mrno }}
                        className="flex w-28 px-4 py-3 bg-[#0FA968] text-black font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                        style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}
                    >
                        <img src={BackArrow} alt="Back" className="mr-2 w-6 h-6" />
                        Back
                    </Link>
                    <span
                        className="ml-4 text-2xl font-semibold"
                        style={{ fontFamily: 'Roboto', fontSize: '28px', fontWeight: '600', lineHeight: '32.81px' }}
                    >
                        Case Sheet
                    </span>
                </div>

                <hr className="my-6 border-gray-300 no-print" />

                <div className="flex justify-end">
                    <div className="receipt-container rounded-lg p-3 w-1/2">
                        <div className="flex flex-row gap-4 mt-12">
                            {/* Patient Details Column */}
                            <div className="w-1/2">
                                <div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Name:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.name || 'N/A'}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>MRN:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.mrno || 'N/A'}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>DOB:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.dob || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Age:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{calculateAge(appointment?.dob)}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Gender:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{formatGender(appointment?.gender)}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Address:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.address || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Appointment Details Column */}
                            <div className="w-1/2">
                                <div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Dept:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.department || 'N/A'}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Doctor:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.doctor || 'N/A'}</span>
                                    </div>
                                    <div className="field-row flex justify-between py-01">
                                        <span className="field-label text-md font-bold text-black" style={{ fontFamily: 'Roboto' }}>Date:</span>
                                        <span className="field-value text-md text-black" style={{ fontFamily: 'Roboto' }}>{appointment?.date || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center gap-4 mt-4 no-print">
                            <button
                                onClick={handlePrint}
                                className="px-3 py-1.5 bg-[#0FA968] text-black font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                                style={{ fontFamily: 'Roboto', fontSize: '13px' }}
                            >
                                Print Case Sheet
                            </button>
                            <Link
                                to="/login"
                                state={{ patientId: appointment?.mrno }}
                                className="px-3 py-1.5 bg-[#EFFEF7] text-[#0FA968] font-semibold rounded-lg shadow hover:bg-[#138F5E] hover:text-black transition duration-200 border border-[#0FA968]"
                                style={{ fontFamily: 'Roboto', fontSize: '13px' }}
                            >
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppointmentReceipt;